/**
 * @file Builds `Charge` entities from backend charge payloads — live responses
 * and stored statement JSON alike. Sole call site of
 * `getPaymentRouteIdForChargeData`, so every charge's `paymentRouteId` is
 * resolved here (legacy boolean-only payloads included) and no
 * reader ever sees an unresolved route.
 *
 * Flow: charge payload → `create(data)` explicit field map (nested payments /
 * attachments via their factories) → `Charge` entity; `reinitialize` re-wraps an
 * already-mapped `.data`.
 * Exceptions: none.
 */
import Factory from "../Factory";
import { FactoryInterface } from "../FactoryInterface";

import { Charge } from "../Entities/Charge/Charge";
import { ChargeInterface } from "../Entities/Charge/ChargeInterface";
import CurrencyContainer from "../../Currency/CurrencyContainer";
import Store from "../../Store/Store";

import { AttachmentInterface } from "../Entities/Attachment/AttachmentInterface";
import AttachmentFactory from "./AttachmentFactory";
import EventFactory from "./EventFactory";
import { PaymentInterface } from "../Entities/Payment/PaymentInterface";
import PaymentFactory from "./PaymentFactory";
import { getPaymentRouteIdForChargeData } from "../Entities/Charge/PaymentRoute";

class ChargeFactory extends Factory implements FactoryInterface {
  public create(data: any): ChargeInterface {
    const attachments: AttachmentInterface[] = AttachmentFactory.createFromArray(data.attachments);
    const linkedPayments: PaymentInterface[] = !!data.linkedPayments && data.linkedPayments.map(payment => payment?.id && PaymentFactory.create(payment));
    const relatedPayments: PaymentInterface[] = !!data.relatedPayments && data.relatedPayments.map(payment => payment?.id && PaymentFactory.create(payment));
    const payments: PaymentInterface[] = !!data.payments && data.payments.map(payment => payment?.id && PaymentFactory.create(payment));


    return new Charge({
      id: data.id,
      amount: data.amount,
      amountPaid: data.amountPaid,
      amountPaidInCurrentPeriod: data.amountPaidInCurrentPeriod,
      companyId: data.companyId,
      contactId: data.contactId,
      ownerId: data.ownerId,
      unitId: data.unitId,
      propertyId: data.propertyId,
      contractId: data.contractId,
      eventId: data.eventId,
      event: !!data.event ? EventFactory.create(data.event) : null,
      description: data.description,
      dueDate: data.dueDate ? new Date(data.dueDate) : null,
      // firstDate - contains the "dateOfStatementPeriod" (indicates the statement in which payment will be included)
      firstDate: data.firstDate ? new Date(data.firstDate) : null,
      lastDate: data.lastDate ? new Date(data.lastDate) : null,
      currencyId: data.currencyId,
      currency: CurrencyContainer.pickFromId(data.currencyId),
      chargeClass: Store.getConfigurator().getState().globalTypes?.ChargeClass?.[data.chargeClassId],
      type: Store.getConfigurator().getState().globalTypes?.ChargeType?.[data.type],
      chargeTemplateId: data.chargeTemplateId,
      paymentDetails: data.paymentDetails,
      paymentIds: Object.keys(data?.paymentDetails || {}),
      automaticallyCreated: data.automaticallyCreated,
      createdAt: new Date(data.createdAt),
      removed: data.removed,
      isSettle: data.isSettle,
      isLocked: data.isLocked,
      isReadOnly: data.isReadOnly,
      period: Store.getConfigurator().getState().globalTypes?.RepeatPeriod?.[data.period],
      invoiceDate: data.invoiceDate ? new Date(data.invoiceDate) : null,
      orderDate: data.orderDate ? new Date(data.orderDate) : null,
      invoiceNumber: data.invoiceNumber,
      vat: data.vat,
      isInvoice: data.isInvoice,
      amazonFolderName: data.amazonFolderName,
      attachments: attachments,
      ownerFinancialStatementId: data.ownerFinancialStatementId,
      chargeClassId: data.chargeClassId,
      agencyId: data.agencyId,
      agencyFeeChargeId: data.agencyFeeChargeId,
      agencyFinancialStatementId: data.agencyFinancialStatementId,
      createdBy: data.createdBy,
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
      updatedBy: data.updatedBy,
      automaticallyRemoved: data.automaticallyRemoved,
      amountPaidToOwner: data.amountPaidToOwner,
      utilityDetails: data.utilityDetails || null,
      relatedTenantCharges: data.relatedTenantCharges || [],
      collectiveChargeTemplateIds: data.collectiveChargeTemplateIds || [],
      previousObjectContractId: data.previousObjectContractId,
      delinquentDay: data.delinquentDay,
      ledgerAccountId: data.ledgerAccountId,
      invoiceReference: data.invoiceReference,
      fullName: data.fullName,
      isMigrationForInvoiceDate: data.isMigrationForInvoiceDate,
      snoozedByUser: data.snoozedByUser,
      isBill: data.isBill,
      isToBeAnInvoice: data.isToBeAnInvoice,
      isCounterpartyAgency: data.isCounterpartyAgency,
      isCounterpartyOwner: data.isCounterpartyOwner,
      isCreatedToSettleOnePaymentWithSavings: data.isCreatedToSettleOnePaymentWithSavings,
      isCreatedToSettleTwoPayments: data.isCreatedToSettleTwoPayments,
      isDeposit: data.isDeposit,
      isPayout: data.isPayout,
      isRefund: data.isRefund,
      isSettlement: data.isSettlement,
      isUnpaid: data.isUnpaid,
      isWithdrawal: data.isWithdrawal,
      isSavings: data.isSavings,
      isObjectOwner: data.isObjectOwner,
      hasOwnerAsObject: data.hasOwnerAsObject,
      hasAgencyAsObject: data.hasAgencyAsObject,
      isPaid: data.isPaid,
      isFullyPaid: data.isFullyPaid,
      isPartiallyPaid: data.isPartiallyPaid,
      isRemoved: data.isRemoved,
      // dateOfPeriod - contains the period for which charge is created
      dateOfPeriod: data.dateOfPeriod ? new Date(data.dateOfPeriod) : null,
      oldestPaymentDate: data.oldestPaymentDate ? new Date(data.oldestPaymentDate) : null,
      financialStatement: data.financialStatement || null,
      note: data.note || "",
      collectiveVat: data.collectiveVat || null,
      isSnoozed: data.isSnoozed,
      isReceivable: data.isReceivable,
      isPayable: data.isPayable,
      isCredit: data.isCredit,
      hasPayoutType: data.hasPayoutType,
      hasSettlementType: data.hasSettlementType,
      hasSavingsType: data.hasSavingsType,
      hasRefundType: data.hasRefundType,
      isCreditType: data.isCreditType,
      isCredited: data.isCredited,
      linkedPayments: linkedPayments || [],
      relatedPayments: relatedPayments || [],
      payments: payments || [],
      snoozeDate: data.snoozeDate || null,
      isReferencedToSavings: data.isReferencedToSavings,
      isPartOfASettlementWithSavings: data.isPartOfASettlementWithSavings,
      hasMemos: data.hasMemos,
      creditCharges: data.creditCharges || [],
      isSent: data.isSent,
      paymentRouteId: getPaymentRouteIdForChargeData(data),
      name: data.name,
      automaticallyLinked: data.automaticallyLinked,
      linkedChargeId: data.linkedChargeId,
      lastDateOfPeriod: data.lastDateOfPeriod ? new Date(data.lastDateOfPeriod) : null,
      settledCharges: data.settledCharges || [],
      settledChargeIds: data.settledChargeIds || [],
      linkedCharge: data.linkedCharge || null,
      chargeTemplates: data.chargeTemplates || [],
    });
  };

  public reinitialize(data: any): ChargeInterface {
    return new Charge({
      ...data
    });
  }
}

export default new ChargeFactory();
